import { UseMonolithApiQueryOptions } from '@/hooks/api/useMonolithApiQuery';
import { SimpleErrorResponse } from '@/models/Error';
import { HtmlReport } from '@/models/HtmlReport';

import useBuildHtmlReports, { UseBuildHtmlReportsProps } from './useBuildHtmlReports';
import usePipelineHtmlReports, { UsePipelineHtmlReportsProps } from './usePipelineHtmlReports';

type UseHtmlReportsProps = {
  appSlug?: string;
  attemptId?: string;
  buildSlug?: string;
  pipelineSlug?: string;
  excludeCategory?: string;
  options?: UseMonolithApiQueryOptions<HtmlReport[], SimpleErrorResponse>;
};

const isUseBuildHtmlReportsProps = (props: UseHtmlReportsProps): props is UseBuildHtmlReportsProps => {
  return !!props.buildSlug && !props.pipelineSlug;
};

const isUsePipelineHtmlReportsProps = (props: UseHtmlReportsProps): props is UsePipelineHtmlReportsProps => {
  return !!(props.appSlug && props.pipelineSlug && props.attemptId);
};

const toUseBuildHtmlReportProps = (props: UseHtmlReportsProps): UseBuildHtmlReportsProps => {
  if (isUseBuildHtmlReportsProps(props)) {
    return props;
  }

  return { buildSlug: '', options: { ...props.options, enabled: false } };
};

const toUsePipelineHtmlReportProps = (props: UseHtmlReportsProps): UsePipelineHtmlReportsProps => {
  if (isUsePipelineHtmlReportsProps(props)) {
    return props;
  }

  return { appSlug: '', attemptId: '', options: { ...props.options, enabled: false }, pipelineSlug: '' };
};

const useHtmlReports = (props: UseHtmlReportsProps) => {
  const buildQueryResult = useBuildHtmlReports(toUseBuildHtmlReportProps(props));
  const pipelineQueryResult = usePipelineHtmlReports(toUsePipelineHtmlReportProps(props));

  if (isUseBuildHtmlReportsProps(props)) {
    return { htmlReports: buildQueryResult.data ?? [], ...buildQueryResult };
  }

  if (isUsePipelineHtmlReportsProps(props)) {
    return { htmlReports: pipelineQueryResult.data ?? [], ...pipelineQueryResult };
  }

  throw new Error('Can not recognize which HTML reports should be fetch.');
};

export default useHtmlReports;
